import { useState } from "react";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { IApiRequest, ICannotPerformReasonResponse } from "@miplatform/types";

export const useCannotPerformReasons = () => {
  const [cannotPerformReasons, setCannotPerformReasons] = useState<
    ICannotPerformReasonResponse[]
  >([]);
  const [recentCannotPerformReasons, setRecentCannotPerformReasons] = useState<
    ICannotPerformReasonResponse[]
  >([]);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");

  const stateManager = useComponentState();
  const debouncedSearch = useDebounce(search);

  const getCannotPerformReasons = ({
    top,
    skip,
    filters,
  }: IApiRequest = {}) => {
    const combinedFilters = {
      ...filters,
      label: { contains: debouncedSearch },
    };

    return stateManager.run(async () => {
      return taskService.cannotPerformReasons.getAll({
        top,
        skip,
        sort: "label",
        filters: combinedFilters,
      });
    });
  };

  const handleDeleteCannotPerformReason = (
    data: ICannotPerformReasonResponse
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.cannotPerformReasons.delete({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.label} was deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  const handleCreateCannotPerformReason = (
    data: Partial<ICannotPerformReasonResponse>
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.cannotPerformReasons.create({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.label} was created successfully.`,
          },
          failure: {
            text: `Failed to create ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  const handleUpdateCannotPerformReason = (
    data: Partial<ICannotPerformReasonResponse>
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.cannotPerformReasons.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.label} was updated successfully.`,
          },
          failure: {
            text: `Failed to update ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    getCannotPerformReasons,
    cannotPerformReasons,
    setCannotPerformReasons,
    setRecentCannotPerformReasons,
    recentCannotPerformReasons,
    handleDeleteCannotPerformReason,
    refresh,
    setSearch,
    search,
    debouncedSearch,
    handleCreateCannotPerformReason,
    handleUpdateCannotPerformReason,
  };
};
